import ky from "ky";
import semver from "semver";
import type { NormalizedOptions } from "ky";
import { SignatureV4 } from "@aws-sdk/signature-v4";
import { Sha256 } from "@aws-crypto/sha256-browser";
import { useAuthStore, useAwardStore } from "./stores";
import { createTRPCClient, httpBatchLink } from "@trpc/client";
import type { AppRouter } from "best-of-awards-api";
import type { AppType } from "best-of-awards-api";
import { hc } from "hono/client";

function getHonoHeaders() {
  const authStore = useAuthStore();
  const awardStore = useAwardStore();
  return {
    "Access-Token": authStore.tokens.AccessToken,
    "boa-table": awardStore.activeAward?.table,
    "x-amz-project": "best-of-awards-dashboard",
  };
}

export const honoClient = hc<AppType>(
  import.meta.env.VITE_API_HONO_URL ?? "http://localhost:3333",
  {
    headers: () => getHonoHeaders(),
  }
);

const trpcClient = createTRPCClient<AppRouter>({
  links: [
    httpBatchLink({
      url: import.meta.env.VITE_TRPC_URL ?? "http://localhost:3333/trpc",
      async headers() {
        const authStore = useAuthStore();
        const awardStore = useAwardStore();
        return {
          ["Access-Token"]: authStore.tokens.AccessToken,
          ["boa-table"]: awardStore.activeAward?.table,
          ["x-amz-project"]: "best-of-awards-admin-dashboard",
        };
      },
    }),
  ],
});

export { trpcClient };

export const privateBoaHttp = ky.extend({
  prefixUrl: import.meta.env.VITE_API_BASE_URL,
  throwHttpErrors: true,
  timeout: 60000,
  retry: {
    methods: ["get", "put", "post", "delete", "options", "head", "trace"],
    limit: 2,
    statusCodes: [403, 408, 413, 429, 500, 502, 503, 504],
  },
  hooks: {
    beforeRequest: [
      // add AWS authentication headers
      async (request, options) => {
        const authStore = useAuthStore();
        const signedRequest = await signRequest(request, options);
        if (signedRequest) {
          for (const h in signedRequest.headers) {
            request.headers.set(h, signedRequest.headers[h]);
          }
          request.headers.set("Access-Token", authStore.tokens.AccessToken);
        }
      },
    ],
    beforeRetry: [
      async ({ request, options }) => {
        const authStore = useAuthStore();
        const { authenticated } = await authStore.login({
          username: "",
          password: "",
        });
        if (!authenticated) {
          return ky.stop;
        }
        const signedRequest = await signRequest(request, options);
        if (signedRequest) {
          for (const h in signedRequest.headers) {
            request.headers.set(h, signedRequest.headers[h]);
          }
          request.headers.set("Access-Token", authStore.tokens.AccessToken);
        }
      },
    ],
    afterResponse: [
      (_request, _options, response) => {
        const latestClientVersion = response.headers.get(
          "boa-latest-client-version"
        );
        if (latestClientVersion && semver.valid(latestClientVersion)) {
          const authStore = useAuthStore();
          authStore.latestClientVersion = latestClientVersion;
        }
      },
    ],
  },
});

// return an extended ky instance that signs HTTP requests for AWS
// and sets the 'boa-table' header
export const publicBoaHttp = ky.extend({
  prefixUrl: import.meta.env.VITE_API_BASE_URL,
  retry: {
    methods: ["get", "put", "post", "delete", "options", "head", "trace"],
    limit: 3,
    statusCodes: [403, 408, 413, 429, 500, 502, 503, 504],
  },
  throwHttpErrors: true,
  timeout: 60000,
  hooks: {
    beforeRequest: [
      async (request) => {
        request.headers.set("x-amz-project", "best-of-awards-admin-dashboard");
        const authStore = useAuthStore();
        request.headers.set("Access-Token", authStore.tokens.AccessToken);
        const awardStore = useAwardStore();
        request.headers.set("boa-table", awardStore.activeAward?.table);
      },
    ],
    afterResponse: [
      (_request, _options, response) => {
        const latestClientVersion = response.headers.get(
          "boa-latest-client-version"
        );
        if (latestClientVersion && semver.valid(latestClientVersion)) {
          const authStore = useAuthStore();
          authStore.latestClientVersion = latestClientVersion;
        }
      },
    ],
  },
});

async function signRequest(request: Request, options: NormalizedOptions) {
  const authStore = useAuthStore();
  try {
    const signer = new SignatureV4({
      credentials: {
        accessKeyId: authStore.credentials.AccessKeyId || "",
        secretAccessKey: authStore.credentials.SecretKey || "",
        sessionToken: authStore.credentials.SessionToken,
      },
      region: import.meta.env.VITE_AWS_REGION || "",
      sha256: Sha256,
      service: "execute-api",
    });

    const awardStore = useAwardStore();
    const headers = {
      accept: "*/*",
      host: import.meta.env.VITE_API_HOST || "",
      "boa-table": awardStore.activeAward.table,
    };
    const path = pathFromUrl(request.url);
    const queryString = request.url.substring(request.url.indexOf("?") + 1);
    const queryParams = queryStringToObject(queryString);

    const signedRequest = await signer.sign({
      method: request.method,
      path,
      headers,
      query: queryParams,
      body: options.body,
      protocol: "https",
      hostname: import.meta.env.VITE_API_HOST || "",
    });

    return signedRequest;
  } catch (error) {
    console.error;
    return null;
  }
}

const queryStringToObject = (queryString: string) => {
  const queryStringParts = queryString.split("&");
  const queryParams: { [key: string]: string } = {};
  for (const part of queryStringParts) {
    const [key, value] = part.split("=");
    if (typeof value !== "undefined") {
      queryParams[key] = decodeURIComponent(value);
    }
  }
  return queryParams;
};

const pathFromUrl = (url: string) => {
  const regex = new RegExp(`${import.meta.env.VITE_API_HOST}([^?]+)`, "i");
  const match = regex.exec(url);
  if (!match || !match[1]) {
    throw new Error(`Failed to match path from url: ${url}`);
  }
  return match[1];
};
